import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Alert, Card, Table, Input, DatePicker, Tag, Typography, Tooltip, Row, Col, Space, Select } from "antd";
import { CheckCircleFilled, MinusOutlined, WarningOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useLoadList, useQuickSearch } from "../../../hooks";
import { salesRevenueApi } from "../../../api/salesRevenueApi";
import AircraftSelector from "../../../components/common/AircraftSelector";

const { RangePicker } = DatePicker;
const { Text, Link } = Typography;

const DEFAULT_RANGE_DAYS = 7;
const PAGE_SIZE = 50;
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

function currentDefaultRange() {
    return {
        start_date: dayjs().subtract(DEFAULT_RANGE_DAYS - 1, "day").format("YYYY-MM-DD"),
        end_date: dayjs().format("YYYY-MM-DD"),
    };
}

function readDateParam(value, fallback) {
    if (!value || !DATE_PATTERN.test(value)) return fallback;
    const parsed = dayjs(value);
    if (!parsed.isValid() || parsed.format("YYYY-MM-DD") !== value) return fallback;
    return value;
}

const STATUS_MAP = {
    SCHEDULED: { color: "blue", text: "计划" },
    DEPARTED: { color: "processing", text: "已起飞" },
    COMPLETED: { color: "green", text: "完成" },
    DELAYED: { color: "orange", text: "延误" },
};

const CARGO_STATUS_TEXT = {
    COMPLETE: { color: "green", text: "完整" },
    PARTIAL: { color: "orange", text: "部分完整" },
    NO_DATA: { color: "default", text: "暂无货运资料" },
};

function buildColumns(onArchive) {
    return [
        {
            title: "航班号", dataIndex: "flight_number", width: 100,
            render: (v, r) => <Link onClick={() => onArchive(r)}>{v || "—"}</Link>,
        },
        { title: "日期", dataIndex: "flight_date", width: 110, render: (v) => (v ? v.slice(0, 10) : "—") },
        {
            title: "航线",
            dataIndex: "route_display",
            width: 140,
            render: (v) => v || "—",
        },
        { title: "飞机号", dataIndex: "aircraft_reg", width: 100, render: (v) => v || "—" },
        {
            title: "状态", dataIndex: "status", width: 90,
            render: (v) => { if (!v) return "—"; const cfg = STATUS_MAP[v] || { text: v }; return <Tag color={cfg.color}>{cfg.text}</Tag>; },
        },
        {
            title: <Tooltip title="装载毛重（CPM 装载报文或附件装载重量表）">装载毛重 (kg)</Tooltip>,
            dataIndex: "gross_weight_kg", width: 120, align: "right",
            render: (v) => (v != null ? v.toLocaleString() : "—"),
        },
        {
            title: <Tooltip title="运单重量（FFM 货物清单或附件舱单）">运单重量 (kg)</Tooltip>,
            dataIndex: "cargo_weight_kg", width: 120, align: "right",
            render: (v) => (v != null ? v.toLocaleString() : "—"),
        },
        {
            title: "ULD", dataIndex: "uld_count", width: 90, align: "right",
            render: (v, r) => {
                const cmp = r.fact_summary?.uld_comparison;
                if (cmp?.status === "MISMATCH") {
                    return (
                        <Tooltip title="CPM 与 FFM 的 ULD 清单不一致">
                            <Space size={4}>
                                <span>{v ?? "—"}</span>
                                <WarningOutlined style={{ color: "#faad14" }} />
                            </Space>
                        </Tooltip>
                    );
                }
                return v ?? "—";
            },
        },
        { title: "运单", dataIndex: "awb_count", width: 70, align: "right", render: (v) => v ?? "—" },
        {
            title: <Tooltip title="有装载类事实（不等于收到标准 CPM 原文）">CPM</Tooltip>,
            dataIndex: "has_cpm", width: 60, align: "center",
            render: (v) => v ? (
                <Tooltip title="有装载类事实（不等于收到标准 CPM 原文）"><CheckCircleFilled style={{ color: "#52c41a" }} /></Tooltip>
            ) : <MinusOutlined style={{ color: "#d9d9d9" }} />,
        },
        {
            title: <Tooltip title="有舱单类事实（不等于收到标准 FFM 原文）">FFM</Tooltip>,
            dataIndex: "has_ffm", width: 60, align: "center",
            render: (v) => v ? (
                <Tooltip title="有舱单类事实（不等于收到标准 FFM 原文）"><CheckCircleFilled style={{ color: "#52c41a" }} /></Tooltip>
            ) : <MinusOutlined style={{ color: "#d9d9d9" }} />,
        },
        {
            title: "文件数", dataIndex: ["file_summary", "total_count"], width: 80, align: "right",
            render: (v) => v ?? 0,
        },
        {
            title: "数据状态", dataIndex: "cargo_status", width: 110,
            render: (v) => { const cfg = CARGO_STATUS_TEXT[v] || { color: "default", text: v || "暂无货运资料" }; return <Tag color={cfg.color}>{cfg.text}</Tag>; },
        },
        {
            title: "操作", width: 90, align: "center", fixed: "right",
            render: (_, r) => <Link onClick={() => onArchive(r)}>查看档案</Link>,
        },
    ];
}

function readUrlState(searchParams) {
    const defaults = currentDefaultRange();
    let startDate = readDateParam(searchParams.get("start_date"), defaults.start_date);
    let endDate = readDateParam(searchParams.get("end_date"), defaults.end_date);
    if (startDate > endDate) {
        [startDate, endDate] = [endDate, startDate];
    }
    return {
        start_date: startDate,
        end_date: endDate,
        flight_number: searchParams.get("flight_number") || undefined,
        aircraft_reg: searchParams.get("aircraft_reg") || undefined,
        awb_number: searchParams.get("awb_number") || undefined,
        uld_id: searchParams.get("uld_id") || undefined,
        data_status: searchParams.get("data_status") || undefined,
        source_type: searchParams.get("source_type") || undefined,
        page: Number(searchParams.get("page")) || 1,
    };
}

export default function SalesCargoReportPage() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const initial = useMemo(() => readUrlState(searchParams), []); // eslint-disable-line react-hooks/exhaustive-deps

    const [filterParams, setFilterParams] = useState({
        start_date: initial.start_date,
        end_date: initial.end_date,
        ...(initial.flight_number ? { flight_number: initial.flight_number } : {}),
        ...(initial.aircraft_reg ? { aircraft_reg: initial.aircraft_reg } : {}),
        ...(initial.awb_number ? { awb_number: initial.awb_number } : {}),
        ...(initial.uld_id ? { uld_id: initial.uld_id } : {}),
        ...(initial.data_status ? { data_status: initial.data_status } : {}),
        ...(initial.source_type ? { source_type: initial.source_type } : {}),
    });
    const [pagination, setPagination] = useState({ current: initial.page, pageSize: PAGE_SIZE });

    // 筛选、日期和分页写回 URL。从档案返回或刷新时，看到的还是刚才那一段。
    useEffect(() => {
        const next = new URLSearchParams();
        for (const k of ["start_date", "end_date", "flight_number", "aircraft_reg", "awb_number", "uld_id", "data_status", "source_type"]) {
            if (filterParams[k]) next.set(k, filterParams[k]);
        }
        if (pagination.current > 1) next.set("page", String(pagination.current));
        const nextQs = next.toString();
        if (searchParams.toString() !== nextQs) {
            setSearchParams(next, { replace: true });
        }
    }, [filterParams, pagination, searchParams, setSearchParams]);

    const { data, isLoading, error } = useLoadList("salesCargoReport", salesRevenueApi.getCargoReportFlights, {
        page: pagination.current,
        page_size: pagination.pageSize,
        ...filterParams,
    });

    const items = error ? [] : (data?.items ?? []);

    const flightNumberSearch = useQuickSearch({
        setFilterParams,
        setPagination,
        paramKey: "flight_number",
        initialValue: initial.flight_number || "",
    });
    const awbNumberSearch = useQuickSearch({
        setFilterParams,
        setPagination,
        paramKey: "awb_number",
        initialValue: initial.awb_number || "",
    });
    const uldIdSearch = useQuickSearch({
        setFilterParams,
        setPagination,
        paramKey: "uld_id",
        initialValue: initial.uld_id || "",
    });

    const handleDateChange = useCallback((dates) => {
        setFilterParams((prev) => {
            const next = { ...prev };
            if (dates?.length === 2) {
                next.start_date = dates[0].format("YYYY-MM-DD");
                next.end_date = dates[1].format("YYYY-MM-DD");
            } else {
                const defaults = currentDefaultRange();
                next.start_date = defaults.start_date;
                next.end_date = defaults.end_date;
            }
            return next;
        });
        setPagination((prev) => ({ ...prev, current: 1 }));
    }, []);

    const handleAircraftChange = useCallback((registrations) => {
        setFilterParams((prev) => {
            const next = { ...prev };
            if (registrations?.length) next.aircraft_reg = registrations.join(",");
            else delete next.aircraft_reg;
            return next;
        });
        setPagination((prev) => ({ ...prev, current: 1 }));
    }, []);

    const handleSelectFilter = useCallback((key, value) => {
        setFilterParams((prev) => {
            const next = { ...prev };
            if (value) next[key] = value;
            else delete next[key];
            return next;
        });
        setPagination((prev) => ({ ...prev, current: 1 }));
    }, []);

    const handleTableChange = useCallback((pag) => {
        setPagination({ current: pag.current, pageSize: pag.pageSize });
    }, []);

    const openArchive = useCallback((record) => {
        navigate(`/sales/cargo-report/flights/${record.flight_operation_id}`);
    }, [navigate]);

    const columns = useMemo(() => buildColumns(openArchive), [openArchive]);

    const dateValue = useMemo(() => {
        if (filterParams.start_date && filterParams.end_date) {
            return [dayjs(filterParams.start_date), dayjs(filterParams.end_date)];
        }
        return null;
    }, [filterParams.start_date, filterParams.end_date]);

    const aircraftValue = useMemo(
        () => (filterParams.aircraft_reg ? filterParams.aircraft_reg.split(",").filter(Boolean) : []),
        [filterParams.aircraft_reg],
    );

    return (
        <div className="page-container">
            <div className="page-header-compact">
                <div className="page-title-compact">货量报告</div>
            </div>

            <Card className="filter-card" size="small">
                <Row gutter={[16, 12]} align="middle">
                    <Col>
                        <Space size={8}>
                            <Text type="secondary">航班号</Text>
                            <Input placeholder="如 CT121" style={{ width: 150 }} {...flightNumberSearch.inputProps} />
                        </Space>
                    </Col>
                    <Col>
                        <Space size={8}>
                            <Text type="secondary">飞机号</Text>
                            <AircraftSelector mode="multiple" value={aircraftValue} onChange={handleAircraftChange}
                                placeholder="选择飞机注册号" maxTagCount="responsive" displayMode="simple" allowClear style={{ width: 220 }} />
                        </Space>
                    </Col>
                    <Col>
                        <Space size={8}>
                            <Text type="secondary">主单号</Text>
                            <Input placeholder="支持部分号码" style={{ width: 170 }} maxLength={30} {...awbNumberSearch.inputProps} />
                        </Space>
                    </Col>
                    <Col>
                        <Space size={8}>
                            <Text type="secondary">板号</Text>
                            <Input placeholder="如 PMC90250" style={{ width: 170 }} maxLength={30} {...uldIdSearch.inputProps} />
                        </Space>
                    </Col>
                    <Col>
                        <Space size={8}>
                            <Text type="secondary">数据状态</Text>
                            <Select
                                allowClear
                                placeholder="全部"
                                style={{ width: 150 }}
                                value={filterParams.data_status}
                                onChange={(v) => handleSelectFilter("data_status", v)}
                                options={[
                                    { value: "COMPLETE", label: "完整" },
                                    { value: "PARTIAL", label: "部分完整" },
                                    { value: "NO_DATA", label: "暂无货运资料" },
                                    { value: "HAS_DATA", label: "有资料" },
                                ]}
                            />
                        </Space>
                    </Col>
                    <Col>
                        <Space size={8}>
                            <Text type="secondary">来源类型</Text>
                            <Select
                                allowClear
                                placeholder="全部"
                                style={{ width: 160 }}
                                value={filterParams.source_type}
                                onChange={(v) => handleSelectFilter("source_type", v)}
                                options={[
                                    { value: "MESSAGE_BODY", label: "标准报文" },
                                    { value: "MAIL_ATTACHMENT", label: "邮件附件" },
                                ]}
                            />
                        </Space>
                    </Col>
                    <Col>
                        <Space size={8}>
                            <Text type="secondary">日期</Text>
                            <RangePicker value={dateValue} onChange={handleDateChange} allowClear={false} style={{ width: 240 }} />
                        </Space>
                    </Col>
                </Row>
            </Card>

            {error ? <Alert type="warning" showIcon message={error} style={{ marginBottom: 12 }} /> : null}
            <Card className="data-card">
                <Table
                    rowKey="flight_operation_id"
                    columns={columns}
                    dataSource={items}
                    loading={isLoading}
                    size="small"
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: error ? 0 : (data?.total ?? 0),
                        showSizeChanger: false,
                        showTotal: (total) => `共 ${total} 个航班`,
                    }}
                    onChange={handleTableChange}
                    locale={{ emptyText: error ? "未能列出航班" : "该条件下暂无航班" }}
                    scroll={{ x: 1250 }}
                />
            </Card>
        </div>
    );
}
